<div class="w-full max-w-full h-full bg-white flex flex-col text-slate-900 overflow-x-hidden box-border" role="dialog"
  aria-modal="true" aria-label="Conduct Task Time Logs">

  <!-- Pinned Drawer Header -->
  <div
    class="shrink-0 bg-white/95 backdrop-blur-md pl-4 sm:pl-5 pr-3 py-3 border-b border-slate-200 flex items-center justify-between gap-2 sm:gap-3">
    <div class="flex items-center gap-3 min-w-0">
      <ngx-avatars [name]="group?.employee_name || 'User'" size="44" [initialsSize]="2" [textSizeRatio]="3"
        class="rounded-full flex-shrink-0">
      </ngx-avatars>
      <div class="min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <h2 class="text-base sm:text-lg font-bold text-slate-900 truncate tracking-tight m-0">
            {{ group?.employee_name }}
          </h2>
          <span *ngIf="isLoadingDesktopTime"
            class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
            <mat-spinner diameter="12" strokeWidth="2"></mat-spinner>
            <span>Syncing timeline...</span>
          </span>
          <span *ngIf="!isLoadingDesktopTime"
            class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border transition-colors"
            [ngClass]="matchBadgeClass">
            <span class="material-symbols-outlined text-sm leading-none">{{ matchBadgeIcon }}</span>
            {{ matchPercentage }}% Matched
          </span>
        </div>
        <p class="text-xs text-slate-500 m-0 mt-0.5 font-normal truncate">
          {{ userRoleSubtitle }} • {{ formattedDateSubtitle }}
        </p>
      </div>
    </div>

    <!-- Right Header Actions (Prev, Next, Close) styled according to SuperSee theme -->
    <div class="flex items-center gap-1.5 flex-shrink-0">
      <button type="button" (click)="onPrevClick()" [disabled]="currentIndex === 0"
        class="!h-8 !px-2.5 !text-sm !font-semibold rounded-md flex items-center justify-center gap-1 border border-black bg-white text-slate-700 hover:border-[#5459d1] hover:text-[#5459d1] hover:bg-[#5459d1]/5 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-700 disabled:hover:bg-white shadow-xs transition cursor-pointer"
        matTooltip="Previous employee">
        <mat-icon class="!text-base leading-none text-slate-400">arrow_upward</mat-icon>
        <span>Prev</span>
      </button>
      <button type="button" (click)="onNextClick()" [disabled]="currentIndex >= groups.length - 1"
        class="!h-8 !px-2.5 !text-sm !font-semibold rounded-md flex items-center justify-center gap-1 border border-black bg-white text-slate-700 hover:border-[#5459d1] hover:text-[#5459d1] hover:bg-[#5459d1]/5 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-700 disabled:hover:bg-white shadow-xs transition cursor-pointer"
        matTooltip="Next employee">
        <mat-icon class="!text-base leading-none text-slate-400">arrow_downward</mat-icon>
        <span>Next</span>
      </button>

      <!-- Vertical divider between navigation and actions -->
      <div class="h-4 w-px bg-slate-200 mx-1"></div>

      <!-- Icon-only Export Button -->
      <button type="button" (click)="exportEmployeeLogs()" [disabled]="sortedLogs.length === 0"
        class="!h-8 !w-8 rounded-lg inline-flex items-center justify-center text-slate-600 hover:text-[#5459d1] hover:bg-[#5459d1]/10 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-600 disabled:hover:bg-transparent transition-colors border-0 bg-transparent cursor-pointer focus:outline-none"
        aria-label="Export Conduct Log" matTooltip="Export conduct logs (CSV)">
        <mat-icon class="!text-lg !w-5 !h-5 leading-none">file_download</mat-icon>
      </button>

      <!-- Clean, borderless close button positioned neatly without excess right margin -->
      <button type="button" (click)="closeDrawer()"
        class="!h-8 !w-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors border-0 bg-transparent cursor-pointer focus:outline-none"
        aria-label="Close drawer" matTooltip="Close (Esc)">
        <mat-icon class="!text-sm !w-4 !h-4 leading-none">close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Scrollable Drawer Body -->
  <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-5 space-y-4 bg-slate-50/50">

    <!-- Daily Breakdown Strip with 7-Day Cards (Visible when multiple dates are selected) -->
    <div *ngIf="isMultipleDates" class="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-xs space-y-4">

      <!-- Header Row: Title & Subtitle + Theme Datepicker with Stepper -->
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 class="text-sm sm:text-base font-bold text-slate-900 m-0 leading-tight">
            Daily breakdown
          </h3>
          <p class="text-xs text-slate-500 m-0 mt-0.5 font-normal">
            Viewing work logged for this date
          </p>
        </div>

        <!-- Theme Based Date Navigator matching SuperSee Theme -->
        <div class="timeline-date-nav shrink-0" role="group" aria-label="Conduct drawer date">
          <button type="button" mat-icon-button class="timeline-nav-btn" [disabled]="isDrawerPrevDateDisabled()"
            (click)="onDrawerPrevDate()" matTooltip="Previous day">
            <mat-icon>keyboard_arrow_left</mat-icon>
          </button>

          <mat-form-field class="date-range-picker-form-field timeline-date-field timeline-date-field--single"
            subscriptSizing="dynamic">
            <input matInput [matDatepicker]="drawerDp" [ngModel]="drawerSelectedDate"
              (ngModelChange)="onDrawerDatePicked($event)" [min]="effectiveStartDate" [max]="effectiveEndDate"
              placeholder="Date" readonly class="cursor-pointer" (click)="drawerDp.open()">
            <mat-datepicker-toggle matIconSuffix [for]="drawerDp"></mat-datepicker-toggle>
            <mat-datepicker #drawerDp></mat-datepicker>
          </mat-form-field>

          <button type="button" mat-icon-button class="timeline-nav-btn" [disabled]="isDrawerNextDateDisabled()"
            (click)="onDrawerNextDate()" matTooltip="Next day">
            <mat-icon>keyboard_arrow_right</mat-icon>
          </button>
        </div>
      </div>

      <!-- 7-Day Card Carousel Strip with Left/Right Navigation Arrows (styled strictly to match design) -->
      <div class="flex items-center gap-1.5 sm:gap-2.5">
        <!-- Left Window Arrow (matches card height with soft squircle corners) -->
        <button type="button" (click)="onPrevDayCard()" [disabled]="isDrawerPrevDateDisabled()"
          class="rounded-md border-none bg-white text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white flex items-center justify-center shrink-0 transition-all cursor-pointer shadow-2xs focus:outline-none"
          matTooltip="Previous day">
          <mat-icon class="!text-base leading-none">chevron_left</mat-icon>
        </button>

        <!-- Dynamic Day Cards Columns Grid (5 days on mobile, 7 days on desktop) -->
        <div class="grid gap-1.5 sm:gap-2.5 flex-1 min-w-0"
          [style.grid-template-columns]="'repeat(' + (visibleDayCards.length || visibleCardWindowSize) + ', minmax(0, 1fr))'">
          <button *ngFor="let card of visibleDayCards; trackBy: trackByDate" type="button"
            (click)="onDrawerDatePicked(card.date)"
            class="!h-20 group relative flex flex-col items-center justify-center px-1 sm:px-2 py-5 rounded-3xl transition-all cursor-pointer select-none text-center focus:outline-none"
            [ngClass]="card.isSelected 
              ? 'border border-solid border-[#5459d1] bg-[#5459d1]/5 shadow-xs' 
              : 'border border-solid border-gray-200 bg-white hover:border-gray-300 hover:bg-slate-50/70 shadow-2xs'"
            [matTooltip]="card.fullLabel">

            <span class="text-[11px] sm:text-xs leading-tight transition-colors"
              [ngClass]="card.isSelected ? 'font-semibold text-[#5459d1]' : 'font-normal text-slate-500'">
              {{ card.dayOfWeek }}
            </span>

            <span class="text-base sm:text-lg leading-tight mt-0.5 sm:mt-1 transition-colors"
              [ngClass]="card.isSelected ? 'font-bold text-[#5459d1]' : 'font-bold text-slate-900'">
              {{ card.dayOfMonth }}
            </span>

            <span class="w-1.5 h-1.5 rounded-full mt-1.5 transition-colors" [ngClass]="card.dotColorClass"></span>
          </button>
        </div>

        <!-- Right Window Arrow (matches card height with soft squircle corners) -->
        <button type="button" (click)="onNextDayCard()" [disabled]="isDrawerNextDateDisabled()"
          class="rounded-md border-none bg-white text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white flex items-center justify-center shrink-0 transition-all cursor-pointer shadow-2xs focus:outline-none"
          matTooltip="Next day">
          <mat-icon class="!text-base leading-none">chevron_right</mat-icon>
        </button>
      </div>
    </div>

    <!-- Summary Metrics Card with Match Donut Chart -->
    <app-conduct-timelogs-summary-card [matchPercentage]="matchPercentage" [desktopTimeFormatted]="desktopTimeFormatted"
      [desktopTimeSubtitle]="desktopTimeSubtitle" [conductTimeFormatted]="conductTimeFormatted"
      [tasksCount]="sortedLogs.length" [unloggedTimeFormatted]="unloggedTimeFormatted"
      [unloggedGapsCount]="unloggedGapsCount" [isLoading]="isLoadingDesktopTime">
    </app-conduct-timelogs-summary-card>

    <!-- SuperSee & Conduct Timeline Card (Base Active/Idle/Offline Track with Overlaid Conduct Task Chips) -->
    <div class="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-xs space-y-4">
      <!-- Card Header: Title & Subtitle + Top-Right Legend -->
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 class="text-base sm:text-lg font-bold text-slate-900 m-0 leading-tight">
            SuperSee & Conduct timeline
          </h3>
          <p class="text-xs text-slate-400 m-0 mt-0.5 font-normal">
            {{ timelineRangeLabel }} · click or hover a task chip for details
          </p>
        </div>

        <!-- Legend (Active, Idle, Offline, Conduct Logged) matching Dashboard & UserProfile Standard -->
        <div class="flex items-center gap-3.5 sm:gap-4 text-xs font-medium text-slate-600 flex-wrap">
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded-full bg-[#4756d6] shrink-0"></span>
            <span>Active</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded-full bg-[#f59e0b] shrink-0"></span>
            <span>Idle</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded-full bg-[#cbd5e1] border border-slate-300 shrink-0"></span>
            <span>Offline</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-3.5 h-2 rounded-full bg-[#111827] shrink-0"></span>
            <span>Conduct logged</span>
          </div>
        </div>
      </div>

      <!-- Single Timeline Track with Base SuperSee Activity and Overlaid Conduct Task Chips (Full Width Column Layout) -->
      <div class="flex flex-col space-y-2 pt-1 w-full">
        <!-- Timeline Bar Container (Full Width) -->
        <div class="w-full h-10 sm:h-11 relative flex items-center">
          <div *ngIf="isLoadingDesktopTime" class="w-full h-full">
            <ngx-skeleton-loader count="1"
              [theme]="{ 'border-radius': '12px', height: '40px', width: '100%', margin: '0' }"></ngx-skeleton-loader>
          </div>

          <div *ngIf="!isLoadingDesktopTime"
            class="w-full h-full bg-[#e9ecf2] rounded-xl relative flex items-center border border-slate-200/80 overflow-hidden shadow-2xs">

            <!-- Layer 1: Real SuperSee Desktop Activity (Active & Idle Segments) -->
            <div *ngFor="let seg of userTimelineSegments; trackBy: trackByTimelineSegment"
              [style.left.%]="seg.leftPercent" [style.width.%]="seg.widthPercent"
              [style.background]="seg.isIdle ? '#f59e0b' : '#4756d6'" [matTooltip]="seg.tooltip"
              matTooltipPosition="above"
              class="absolute top-1 bottom-1 rounded-md transition-opacity hover:opacity-90 cursor-pointer pointer-events-auto">
            </div>

            <!-- Layer 2: Chip-based Task Timeline (Conduct Tasks Overlaid) -->
            <div *ngFor="let seg of taskLogSegments; trackBy: trackByTaskSegment" [style.left.%]="seg.leftPercent"
              [style.width.%]="seg.widthPercent"
              class="absolute top-0 bottom-0 flex items-center z-10 pointer-events-none">

              <!-- Left Whisker Tick (|) -->
              <div class="absolute left-0 top-1 bottom-1 w-[2px] rounded-full z-20 pointer-events-none shadow-xs"
                [style.background]="seg.hexColor || '#111827'"></div>

              <!-- Horizontal Connecting Caliper Hairline -->
              <div
                class="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[1.5px] pointer-events-none z-10 opacity-70"
                [style.background]="seg.hexColor || '#111827'"></div>

              <!-- Floating Task Chip Pill -->
              <div [id]="'task-chip-' + (seg.taskId || seg.startMin)" (click)="onTaskChipClick(seg, $event)"
                (mouseenter)="onTaskChipMouseEnter(seg, $event)" (mouseleave)="onTaskChipMouseLeave()"
                [matTooltip]="seg.tooltip" matTooltipPosition="above"
                class="task-chip-pill relative z-20 mx-auto px-2.5 h-[24px] sm:h-[26px] max-w-[96%] min-w-[32px] text-white rounded-full ring-2 ring-white/95 shadow-md flex items-center justify-center gap-1 cursor-pointer transition-all hover:scale-[1.03] pointer-events-auto select-none"
                [style.background]="seg.hexColor || '#111827'"
                [ngClass]="{ 'ring-4 ring-indigo-300 shadow-lg scale-[1.03]': activeTaskChip === seg }">
                <span class="text-[10px] sm:text-[11px] font-semibold truncate leading-none text-white tracking-tight">
                  {{ seg.projectName || seg.taskTitle }}
                </span>
              </div>

              <!-- Right Whisker Tick (|) -->
              <div class="absolute right-0 top-1 bottom-1 w-[2px] rounded-full z-20 pointer-events-none shadow-xs"
                [style.background]="seg.hexColor || '#111827'"></div>
            </div>

          </div>
        </div>

        <!-- Bottom Ruler Row: Timeline Ticks Scale (Full Width) -->
        <div class="w-full relative h-5 text-xs text-slate-400 font-medium">
          <span *ngFor="let tick of timelineTicks; trackBy: trackByTick" class="absolute whitespace-nowrap select-none"
            [ngClass]="tick.leftPercent === 0 ? 'left-0 translate-x-0' : (tick.leftPercent >= 99 ? 'right-0 translate-x-0' : '-translate-x-1/2')"
            [style.left.%]="tick.leftPercent < 99 ? tick.leftPercent : null"
            [style.right.px]="tick.leftPercent >= 99 ? 0 : null">
            {{ tick.label }}
          </span>
        </div>
      </div>
    </div>

    <!-- Conduct Task Time Log White-Theme Tooltip Popover -->
    <div *ngIf="activeTaskChip" class="conduct-task-white-tooltip" [style.left.px]="taskTooltipX"
      [style.top.px]="taskTooltipY" (click)="$event.stopPropagation()">

      <div class="conduct-tooltip-card">
        <!-- Header: Task Unique ID Badge + Project Name -->
        <div class="conduct-tooltip-header">
          <span class="conduct-tooltip-project truncate">
            {{ activeTaskChip.projectName || 'General Project' }}
          </span>
          <button type="button" class="conduct-tooltip-close" (click)="closeTaskTooltip()" aria-label="Close details">
            <mat-icon class="!text-[14px] !w-3.5 !h-3.5 leading-none">close</mat-icon>
          </button>
        </div>

        <!-- Task Title -->
        <div class="conduct-tooltip-title">
          {{ activeTaskChip.taskTitle }}
        </div>

        <!-- Section 1: Conduct Task Time Logs -->
        <div class="conduct-tooltip-section">
          <div class="conduct-tooltip-section-title">
            Logged on Conduct
          </div>
          <div class="conduct-tooltip-table-row">
            <span class="conduct-tooltip-time-range">{{ activeTaskChip.timeRangeText }}</span>
            <span class="conduct-tooltip-logged-time">{{ activeTaskChip.durationText }} logged</span>
          </div>
        </div>

        <!-- Section 2: SuperSee Time Tracked -->
        <div class="conduct-tooltip-section">
          <div class="conduct-tooltip-section-title">
            Tracked by SuperSee
          </div>
          <div class="conduct-tooltip-table-row">
            <span class="conduct-tooltip-time-range">{{ activeTaskChip.superSeeTimeRangeText }}</span>
            <span class="conduct-tooltip-logged-time">{{ activeTaskChip.activeOverlapText }} active</span>
          </div>
          <div class="conduct-tooltip-table-row mt-1" *ngIf="activeTaskChip.idleSecs > 0">
            <span class="conduct-tooltip-time-range text-slate-500">Idle</span>
            <span class="conduct-tooltip-logged-time text-slate-600 font-medium">{{ activeTaskChip.idleText }}
              idle</span>
          </div>
          <div class="conduct-tooltip-table-row mt-1" *ngIf="activeTaskChip.offlineSecs > 0">
            <span class="conduct-tooltip-time-range text-slate-500">Offline</span>
            <span class="conduct-tooltip-logged-time text-slate-500 font-medium">{{ activeTaskChip.offlineText }}
              offline</span>
          </div>
        </div>

        <!-- Section 3: Time Difference Note (shown only when difference exists) -->
        <div class="conduct-tooltip-diff-note" *ngIf="activeTaskChip.differenceSecs > 0">
          <span>Time Difference (SuperSee & Conduct): {{ activeTaskChip.differenceText }}</span>
        </div>

        <!-- Divider -->
        <div class="conduct-tooltip-divider"></div>

        <!-- Bottom Right Action: Work Replay -->
        <div class="conduct-tooltip-footer">
          <a class="conduct-tooltip-replay-link" (click)="navigateToWorkReplay(activeTaskChip.log)"
            matTooltip="See insights on work replay page">
            <span>Work Replay</span>
            <svg class="conduct-tooltip-arrow-icon" viewBox="0 0 16 16" fill="currentColor">
              <path fill-rule="evenodd"
                d="M1 8a.75.75 0 0 1 .75-.75h10.69L8.72 3.53a.75.75 0 0 1 1.06-1.06l5 5a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 1 1-1.06-1.06l3.72-3.72H1.75A.75.75 0 0 1 1 8Z"
                clip-rule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Conduct Task Details Section (Modern Tabular Timeline Design) -->
    <div class="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-xs space-y-3">
      <!-- Section Header with Count Badge & Total Logged Time -->
      <div class="flex items-center justify-between gap-2 flex-wrap pb-1">
        <div class="flex items-center gap-2.5">
          <h3 class="text-base sm:text-lg font-bold text-slate-900 m-0 leading-tight">
            Conduct task details
          </h3>
          <span
            class="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600 rounded-full">
            {{ sortedLogs.length }}
          </span>
        </div>

        <span class="text-xs sm:text-sm font-medium text-slate-500">
          Total logged {{ timelineConductHours }}
        </span>
      </div>

      <!-- Tabular Chronological Feed -->
      <div class="w-full overflow-x-auto">
        <table class="w-full text-left border-collapse min-w-[460px]">
          <thead>
            <tr class="border-b border-slate-100 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              <th scope="col" class="py-2.5 pr-4 font-bold w-[32%]">TIME</th>
              <th scope="col" class="py-2.5 px-4 font-bold">TASK</th>
              <th scope="col" class="py-2.5 pl-4 font-bold text-right w-[18%]">LOGGED</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100" *ngIf="interleavedFeed.length > 0">
            <tr *ngFor="let item of interleavedFeed; trackBy: trackByFeedItem"
              [id]="'task-row-' + (item.taskUniqueId || item.log?.task_unique_id || item.log?.task_id || item.startMin)"
              (click)="onTableRowClick(item)" class="transition-colors cursor-pointer hover:bg-slate-50/70"
              [ngClass]="activeTaskChip?.log === item.log ? '!bg-slate-100 ring-1 ring-[#111827]/30' : (item.type === 'task' ? 'bg-white' : 'bg-transparent')">

              <!-- TIME column -->
              <td class="py-3.5 pr-4 align-top whitespace-nowrap text-xs sm:text-sm"
                [ngClass]="item.type === 'task' ? 'text-slate-700 font-medium' : 'text-slate-400 font-normal'">
                {{ item.timeRange }}
              </td>

              <!-- TASK column -->
              <td class="py-3.5 px-4 align-top">
                <!-- Conduct Logged Task Row -->
                <div *ngIf="item.type === 'task'" class="flex flex-col gap-1">
                  <div class="text-xs sm:text-sm font-semibold text-slate-900 leading-snug">
                    {{ item.taskTitle }}
                  </div>
                  <div *ngIf="item.projectName && item.projectName !== 'General Project'"
                    class="flex items-center gap-1.5 mt-0.5">
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border"
                      [ngClass]="[item.projectPalette?.tagBg || 'bg-slate-50', item.projectPalette?.tagText || 'text-slate-600', item.projectPalette?.tagBorder || 'border-slate-200']">
                      <mat-icon class="!text-[12px] !w-3 !h-3 leading-none">folder</mat-icon>
                      <span>{{ item.projectName }}</span>
                    </span>
                  </div>
                </div>

                <!-- Unlogged Tracked Gap Row -->
                <div *ngIf="item.type === 'unlogged'"
                  class="flex items-center gap-1.5 text-xs sm:text-sm font-normal text-slate-400">
                  <mat-icon class="!text-sm !w-4 !h-4 leading-none text-slate-400 shrink-0">info_outline</mat-icon>
                  <span>Tracked, not logged</span>
                </div>
              </td>

              <!-- LOGGED duration column -->
              <td class="py-3.5 pl-4 align-top text-right whitespace-nowrap">
                <!-- Conduct Logged Task Row -->
                <div *ngIf="item.type === 'task'" class="flex flex-col items-end gap-1">
                  <span class="text-xs sm:text-sm font-bold text-slate-900 leading-snug">
                    {{ item.durationBadge }}
                  </span>
                  <div class="flex items-center mt-0.5">
                    <a (click)="navigateToWorkReplay(item.log); $event.stopPropagation()"
                      class="conduct-tooltip-replay-link text-[11px]" matTooltip="Open Work Replay for this task">
                      <span>Work Replay</span>
                      <svg class="conduct-tooltip-arrow-icon" viewBox="0 0 16 16" fill="currentColor">
                        <path fill-rule="evenodd"
                          d="M1 8a.75.75 0 0 1 .75-.75h10.69L8.72 3.53a.75.75 0 0 1 1.06-1.06l5.25 5.25a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 1 1-1.06-1.06l3.72-3.72H1.75A.75.75 0 0 1 1 8Z"
                          clip-rule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>

                <!-- Unlogged Tracked Gap Row -->
                <div *ngIf="item.type === 'unlogged'" class="text-xs sm:text-sm font-normal text-slate-400">
                  {{ item.durationLabel }}
                </div>
              </td>

            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div *ngIf="interleavedFeed.length === 0" class="py-8 text-center text-slate-400 italic text-sm">
          No conduct task logs recorded for this employee on this date.
        </div>
      </div>
    </div>
  </div>

</div>